import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton } from "../components/Primitives";
import ViewToggle, { usePersistedView } from "../components/ViewToggle";
import { Plus, Download } from "lucide-react";
import { toast } from "sonner";
import { usePermission } from "../contexts/AuthContext";
import {
  ROLE_OPTIONS, STATUS_OPTIONS, emptyGuard, derivedStatus,
} from "../components/officers/officerUtils";
import { GuardsFilters } from "../components/officers/OfficersFilters";
import { GuardsTable } from "../components/officers/OfficersTable";
import { GuardsGrid } from "../components/officers/OfficersGrid";
import { GuardFormModal } from "../components/officers/OfficerFormModal";
import { GuardDocsModal } from "../components/officers/OfficerDocsModal";

export default function Officers() {
  const canCreateGuard = usePermission("guards:create");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyGuard);
  const [certKeys, setCertKeys] = useState([]);
  const [docsFor, setDocsFor] = useState(null);
  const [docs, setDocs] = useState([]);

  // Filters
  const [nameFilter, setNameFilter] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [expiredOnly, setExpiredOnly] = useState(false);

  // Deep-link: /guards?expired=1 auto-enables the expired-certs filter.
  // /guards?guard=<id> auto-opens the guard-edit drawer for that guard once
  // the roster has loaded — used by the Site Scorecard "click offender"
  // flow so a CSO can jump straight from a breach to that guard's file.
  const [searchParams, setSearchParams] = useSearchParams();

  // iter242 hotfix — declared BEFORE the deep-link effect that captures it.
  // Otherwise React runs the render pass, hits the effect closure, and blows
  // up with "Cannot access 'onEdit' before initialization" (const TDZ).
  const onEdit = useCallback((g) => {
    setEditing(g);
    const normalizedCerts = (g.certifications || []).map((c) =>
      typeof c === "string"
        ? { name: c, no_expiry: true, issued_at: "", expires_at: "" }
        : { name: c.name || "", no_expiry: !!c.no_expiry, issued_at: c.issued_at || "", expires_at: c.expires_at || "" }
    );
    setForm({
      ...emptyGuard,
      ...g,
      max_weekly_hours: g.max_weekly_hours == null ? "" : g.max_weekly_hours,
      password: "",
      role: g.role || "guard",
      certifications: normalizedCerts,
    });
    setCertKeys(normalizedCerts.map((_, i) => `c-load-${g.id || 'new'}-${i}-${Math.random().toString(36).slice(2, 7)}`));
    setOpen(true);
  }, []);

  useEffect(() => {
    if (searchParams.get("expired") === "1") setExpiredOnly(true);
  }, [searchParams]);
  useEffect(() => {
    const focus = searchParams.get("guard");
    if (!focus || !items.length) return;
    const target = items.find((g) => g.id === focus);
    if (target) {
      onEdit(target);
      // strip the query param so hitting refresh doesn't re-open every time
      const next = new URLSearchParams(searchParams);
      next.delete("guard");
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams, setSearchParams, onEdit]);

  // Sort
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const load = useCallback(async () => {
    const r = await api.get("/guards");
    setItems(r.data);
  }, []);
  const [offices, setOffices] = useState([]);
  useEffect(() => {
    api.get("/offices").then((r) => setOffices(r.data || [])).catch(() => setOffices([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDocs = async (g) => {
    setDocsFor(g);
    const r = await api.get(`/guards/${g.id}/documents`);
    setDocs(r.data);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docsFor) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/guards/${docsFor.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      const r = await api.get(`/guards/${docsFor.id}/documents`);
      setDocs(r.data);
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const buildPayload = () => {
    const p = {
      name: form.name.trim(),
      badge_no: form.badge_no.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      role: form.role || "guard",
      hourly_rate: Number(form.hourly_rate) || 0,
      status: form.status,
      hire_date: form.hire_date || null,
      notes: form.notes || null,
      deactivation_date: form.deactivation_date || null,
      reactivation_date: form.reactivation_date || null,
      office_id: form.office_id || null,
      max_weekly_hours: form.max_weekly_hours === "" || form.max_weekly_hours == null
        ? null
        : Math.max(0, parseInt(form.max_weekly_hours, 10) || 0),
      certifications: Array.isArray(form.certifications)
        ? form.certifications
        : typeof form.certifications === "string"
          ? form.certifications.split(",").map((s) => s.trim()).filter(Boolean).map((n) => ({ name: n, no_expiry: true }))
          : [],
    };
    if (form.username) p.username = form.username.trim().toLowerCase();
    if (form.password) p.password = form.password;
    return p;
  };

  const onSave = async () => {
    if (!form.name?.trim() || !form.badge_no?.trim()) return toast.error("Name and badge number required");
    if (!form.phone?.trim()) return toast.error("Phone number is required");
    if (!form.email?.trim()) return toast.error("Email is required");
    if (form.username && !editing && !form.password) return toast.error("Password is required when adding a  login");
    if (form.password && form.password.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      const payload = buildPayload();
      if (editing) await api.put(`/guards/${editing.id}`, payload);
      else await api.post("/guards", payload);
      toast.success(editing ? "Officer updated" : "Officer added to roster");
      setOpen(false);
      setEditing(null);
      setForm(emptyGuard);
      load();
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
  };

  const onDelete = async (id) => {
    const g = items.find((x) => x.id === id);
    if (!window.confirm(`Deactivate ${g?.name || "guard"}? They'll lose mobile-app access and disappear from active rosters, but their history is preserved.`)) return;
    try {
      await api.delete(`/guards/${id}`);
      toast.success("Officer deactivated");
      load();
    } catch (e) { toast.error(errorMessage(e, "Deactivation failed")); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/guards/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "guards.csv"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errorMessage(e, "Export failed")); }
  };

  // Cross-faceted filter options — narrow each facet by the OTHER active facets.
  const _applyExcept = useCallback((except) => {
    const nameSet = new Set(nameFilter);
    const roleSet = new Set(roleFilter);
    const statusSet = new Set(statusFilter);
    return items.filter((g) => {
      if (except !== "name" && nameSet.size && !nameSet.has(g.id)) return false;
      if (except !== "role" && roleSet.size && !roleSet.has(g.role || "guard")) return false;
      if (except !== "status" && statusSet.size && !statusSet.has(derivedStatus(g))) return false;
      return true;
    });
  }, [items, nameFilter, roleFilter, statusFilter]);

  const nameOptions = useMemo(
    () => _applyExcept("name")
      .map((g) => ({ value: g.id, label: g.name, sub: g.badge_no || "" }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [_applyExcept]
  );
  const roleOptions = useMemo(() => {
    const allowed = new Set(_applyExcept("role").map((g) => g.role || "guard"));
    return ROLE_OPTIONS.filter((o) => allowed.has(o.value));
  }, [_applyExcept]);
  const statusOptions = useMemo(() => {
    const allowed = new Set(_applyExcept("status").map((g) => derivedStatus(g)));
    return STATUS_OPTIONS.filter((o) => allowed.has(o.value));
  }, [_applyExcept]);

  // Self-heal: prune selected values that no longer exist post-narrowing.
  useEffect(() => {
    const valid = new Set(nameOptions.map((o) => o.value));
    if (nameFilter.some((v) => !valid.has(v))) setNameFilter(nameFilter.filter((v) => valid.has(v)));
  }, [nameOptions, nameFilter]);
  useEffect(() => {
    const valid = new Set(roleOptions.map((o) => o.value));
    if (roleFilter.some((v) => !valid.has(v))) setRoleFilter(roleFilter.filter((v) => valid.has(v)));
  }, [roleOptions, roleFilter]);
  useEffect(() => {
    const valid = new Set(statusOptions.map((o) => o.value));
    if (statusFilter.some((v) => !valid.has(v))) setStatusFilter(statusFilter.filter((v) => valid.has(v)));
  }, [statusOptions, statusFilter]);

  // Apply filters
  const filteredItems = useMemo(() => {
    const nameSet = new Set(nameFilter);
    const roleSet = new Set(roleFilter);
    const statusSet = new Set(statusFilter);
    const todayIso = new Date().toISOString().slice(0, 10);
    return items.filter((g) => {
      if (nameSet.size && !nameSet.has(g.id)) return false;
      if (roleSet.size && !roleSet.has(g.role || "guard")) return false;
      if (statusSet.size && !statusSet.has(derivedStatus(g))) return false;
      if (expiredOnly) {
        const hasExpired = (g.certifications || []).some(
          (c) => c && !c.no_expiry && c.expires_at && String(c.expires_at).slice(0, 10) < todayIso
        );
        if (!hasExpired) return false;
      }
      return true;
    });
  }, [items, nameFilter, roleFilter, statusFilter, expiredOnly]);

  // Apply sort
  const sortedItems = useMemo(() => {
    const accessor = (g) => {
      switch (sortKey) {
        case "badge_no": return (g.badge_no || "").toLowerCase();
        case "name": return (g.name || "").toLowerCase();
        case "role": return (g.role || "guard").toLowerCase();
        case "phone": return (g.phone || "").toLowerCase();
        case "hourly_rate": return Number(g.hourly_rate) || 0;
        case "status": return (g.status || "").toLowerCase();
        default: return "";
      }
    };
    const arr = [...filteredItems];
    arr.sort((a, b) => {
      const av = accessor(a), bv = accessor(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredItems, sortKey, sortDir]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const [view, setView] = usePersistedView("guards_view");

  return (
    <div>
      <PageHeader
        title="Officers"
        subtitle="Operational personnel roster. Track certifications and availability."
        action={
          <div className="inline-flex items-center gap-3">
            <ViewToggle testIdPrefix="guards" value={view} onChange={setView} />
            <GhostButton data-testid="guards-export-csv" onClick={exportCsv} title="Download  as CSV">
              <Download className="size-4" /> CSV
            </GhostButton>
            <ActionButton
              data-testid="add-guard-button"
              onClick={() => { setEditing(null); setForm(emptyGuard); setCertKeys([]); setOpen(true); }}
              style={!canCreateGuard ? { display: "none" } : undefined}
            >
              <Plus className="size-4" /> New Officer
            </ActionButton>
          </div>
        }
      />

      <div className="p-8 space-y-4">
        <GuardsFilters
          nameOptions={nameOptions} nameFilter={nameFilter} setNameFilter={setNameFilter}
          roleOptions={roleOptions} roleFilter={roleFilter} setRoleFilter={setRoleFilter}
          statusOptions={statusOptions} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          expiredOnly={expiredOnly} setExpiredOnly={setExpiredOnly}
          visibleCount={sortedItems.length} totalCount={items.length}
        />

        {view === "list" ? (
          <GuardsTable
            items={sortedItems}
            totalCount={items.length}
            sortKey={sortKey} sortDir={sortDir} onSort={onSort}
            onEdit={onEdit} onDelete={onDelete} onOpenDocs={openDocs}
          />
        ) : (
          <GuardsGrid
            items={sortedItems}
            totalCount={items.length}
            onEdit={onEdit} onDelete={onDelete}
          />
        )}
      </div>

      {open && (
        <GuardFormModal
          editing={editing}
          form={form} setForm={setForm}
          certKeys={certKeys} setCertKeys={setCertKeys}
          offices={offices}
          onClose={() => setOpen(false)}
          onSave={onSave}
          onSuccessAndClose={() => { setOpen(false); setEditing(null); load(); }}
        />
      )}

      {docsFor && (
        <GuardDocsModal
          guard={docsFor}
          docs={docs}
          onUpload={onUpload}
          onClose={() => setDocsFor(null)}
        />
      )}
    </div>
  );
}
